import { ClipboardList, FileText, Package, Settings2 } from 'lucide-react';
import { Card } from './Card';
import { DiffSummaryCard } from './DiffSummaryCard';
import { EmptyState } from './EmptyState';
import { StatCard } from './StatCard';
import type { RunDiffResponse, SystemDiff } from '@/hooks/useRuns';

interface Props {
  diff: RunDiffResponse;
  className?: string;
}

/**
 * DiffPane - aggregierte Diff-Anzeige für einen Run (Sprint 6 Mount).
 *
 * Layout:
 *  - Top-Row: 3 StatCards (Files / Pakete / Services) über alle Systeme
 *  - DiffSummaryCard pro System (collapsed-by-default)
 *  - "truncated"-Hinweis falls Backend abgekürzt hat
 *
 * Wird im Run-Detail-Tab "Diff" gerendert (zwischen Live und Steps).
 */
export function DiffPane({ diff, className }: Props): JSX.Element {
  const totals = sumDiff(diff.systems);

  if (diff.systems.length === 0) {
    return (
      <Card className={className}>
        <EmptyState
          icon={<FileText className="size-8" />}
          title="Kein Diff verfügbar"
          description="Backend hat für diesen Run kein Diff-Aggregat geliefert (z.B. weil der Run noch läuft oder keine Änderungen gemacht hat)."
        />
      </Card>
    );
  }

  return (
    <div className={['flex flex-col gap-3', className ?? ''].join(' ')}>
      <div
        className={[
          'grid grid-cols-1 gap-2',
          totals.results > 0 ? 'sm:grid-cols-4' : 'sm:grid-cols-3',
        ].join(' ')}
      >
        <StatCard
          label="Files geändert"
          value={totals.files}
          icon={<FileText className="size-4" />}
          tone={totals.files > 0 ? 'primary' : 'neutral'}
        />
        <StatCard
          label="Pakete"
          value={totals.packages}
          icon={<Package className="size-4" />}
          tone={totals.packages > 0 ? 'primary' : 'neutral'}
        />
        <StatCard
          label="Services restarted"
          value={totals.services}
          icon={<Settings2 className="size-4" />}
          tone={totals.services > 0 ? 'warning' : 'neutral'}
        />
        {/* Nur zeigen, wenn es welche gibt: die Karte "0 Ergebnisse" wäre
            auf jedem Server-Lauf Dauer-Rauschen. */}
        {totals.results > 0 && (
          <StatCard
            label="Routine-Ergebnisse"
            value={totals.results}
            icon={<ClipboardList className="size-4" />}
            tone="primary"
          />
        )}
      </div>

      {diff.truncated && (
        <p
          role="status"
          className="rounded-md border border-warning/30 bg-warning/5 p-2 text-xs text-warning"
        >
          Diff ist abgeschnitten (zu viele Einträge). Vollständiges Diff im Audit-Log.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {diff.systems.map((sys) => (
          <DiffSummaryCard key={sys.system_id} diff={sys} />
        ))}
      </div>
    </div>
  );
}

function sumDiff(systems: SystemDiff[]): {
  files: number;
  packages: number;
  services: number;
  /** Geerntete Result-Blöcke (diff.other) - bei Client-Runs oft der einzige Inhalt. */
  results: number;
} {
  let files = 0;
  let packages = 0;
  let services = 0;
  let results = 0;
  for (const s of systems) {
    files += s.changed_files.length;
    packages += s.changed_packages.length;
    services += s.changed_services.length;
    results += s.other?.length ?? 0;
  }
  return { files, packages, services, results };
}
